import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { ItemFormErrorWithTranslatorComponent } from '../../../components/item-form-error-with-translator/item-form-error-with-translator.component';
import { ItemInfoPopoverComponent } from '../../../components/item-info-popover/item-info-popover.component';
import { SiffiLanguagePickerComponent } from '../../../components/siffi-language-picker/siffi-language-picker.component';
import { TranslateUnauthenticatedPipe } from '../../../filters/translate-unauthenticated.pipe';
import AngularFilterHelperService from '../../../services/angular-filter-helper.service';
import AngularRoutingHelperService from '../../../services/angular-routing-helper.service';
import AppLanguageService from '../../../services/app-language.service';
import PartnerApplicationRestService from '../../../services/partner-application-rest.service';
import Config from '../../../utils/config.service';
import Session from '../../../utils/session.service';

type UploadField = 'certificate' | 'diploma';

@Component({
	standalone: true,
	selector: 'partner-signup',
	templateUrl: './partner-signup.component.html',
	styleUrl: './partner-signup.component.scss',
	imports: [CommonModule, FormsModule, MatSelectModule, TranslateUnauthenticatedPipe, SiffiLanguagePickerComponent, ItemFormErrorWithTranslatorComponent, ItemInfoPopoverComponent]
})
export default class PartnerSignupComponent implements OnInit, OnDestroy {
	private config: Config = inject(Config);
	private session: Session = inject(Session);
	private partnerApplicationRestService: PartnerApplicationRestService = inject(PartnerApplicationRestService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private appLanguageService: AppLanguageService = inject(AppLanguageService);

	private titleSubscription?: Subscription;
	private readonly maxFileSizeInBytes: number = 10 * 1024 * 1024;
	private readonly allowedFileExtensions: Array<string> = ['pdf', 'jpg', 'jpeg', 'png'];
	private readonly vatRatePattern: RegExp = /^\d{1,2}(\.\d0?)?$/;

	product: string = 'SIFFI';
	headerClass: string = 'o-container c-container';
	origin: string = 'siffi.com';
	brandName: string = 'Siffi';
	locale: string = 'en_GB';
	locales: Array<any> = [];
	countries: Array<any> = [];
	logoSuffix: string = 'en';
	langOpen: boolean = false;
	isSubmitting: boolean = false;
	submitFailed: boolean = false;
	submitErrorKey: string = 'PartnerSignup.Submit_Failed';
	formSubmitted: boolean = false;
	selectedCountry: any = undefined;

	formData: any = {
		fullName: '',
		legalName: '',
		regCode: '',
		legalAddress: '',
		vatNumber: '',
		vatRate: '',
		linkedin: '',
		website: '',
		email: '',
		phone: '',
		referenceCustomer: '',
		bio: ''
	};

	qualificationCriteria: Array<string> = [
		'PartnerSignup.Criteria_1',
		'PartnerSignup.Criteria_2',
		'PartnerSignup.Criteria_3',
		'PartnerSignup.Criteria_4'
	];

	uploads: Record<UploadField, any> = {
		certificate: this.createUploadState(),
		diploma: this.createUploadState()
	};

	ngOnInit() {
		var queryParams: any = this.angularRoutingHelperService.getQueryParams();
		this.determineLocation(queryParams);
		this.locale = this.appLanguageService.normalizeLocale(queryParams['locale'] || window.sessionStorage.getItem('locale') || 'en_GB');
		window.sessionStorage.setItem('locale', this.locale);
		this.logoSuffix = this.resolveLogoSuffix(this.locale);
		this.locales = this.appLanguageService.getLanguagesByOrigin(this.origin);
		var currentSession: any = this.session.getSession();
		if (currentSession) {
			this.angularRoutingHelperService.home(currentSession.user);
			return;
		}

		this.countries = this.config.getCountries().map((item) => {
			return {
				label: item.name,
				value: item.code
			};
		});

		this.titleSubscription = this.angularFilterHelperService.watchDocumentTitleUnauthenticated('PartnerSignup.Page_Title');
	}

	ngOnDestroy() {
		if (this.titleSubscription) {
			this.titleSubscription.unsubscribe();
		}
	}

	setLocale(locale) {
		this.angularRoutingHelperService.setQueryParam('locale', locale);
		setTimeout(() => {
			window.location.reload();
		}, 300);
	}

	toggleLangMenu() {
		this.langOpen = !this.langOpen;
	}

	getLocaleOption(locale) {
		return this.appLanguageService.getLanguage(locale);
	}

	getTitleKey() {
		return this.product === 'SIFFI' ? 'PartnerSignup.Title_Siffi' : 'PartnerSignup.Title_MinuDoc';
	}

	navigateToRoot($event) {
		$event.preventDefault();
		window.location.href = this.config.getPublicWebHomeUrl(this.origin, this.locale);
	}

	onFileSelected(field: UploadField, event) {
		var input: any = event.target as HTMLInputElement;
		var files: Array<File> = input.files ? Array.from(input.files) : [];
		if (!files.length) {
			return;
		}

		this.uploads[field].errorKey = undefined;
		this.uploads[field].uploading = true;
		this.uploadSelectedFiles(field, files, 0, input);
	}

	submit(form: NgForm) {
		this.formSubmitted = true;
		this.submitFailed = false;
		this.submitErrorKey = 'PartnerSignup.Submit_Failed';

		var hasRequiredUploads: boolean = this.hasUploadedFile('certificate') && this.hasUploadedFile('diploma');
		var hasUploadingFiles: boolean = this.isUploading('certificate') || this.isUploading('diploma');
		if (this.isSubmitting || form.invalid || !this.selectedCountry || !this.hasProfileLink() || this.hasBioLengthError() || !hasRequiredUploads || hasUploadingFiles || this.hasVatRateError()) {
			return;
		}

		this.isSubmitting = true;
		var payload: any = {
			fullName: this.formData.fullName.trim(),
			legalName: this.formData.legalName.trim(),
			registrationCode: this.formData.regCode.trim(),
			legalAddress: this.formData.legalAddress.trim(),
			vatNumber: this.formData.vatNumber.trim(),
			vatRate: this.formData.vatNumber.trim() ? this.normalizeVatRate() : '',
			countryCode: this.selectedCountry.value,
			linkedinUrl: this.formData.linkedin.trim(),
			websiteUrl: this.formData.website.trim(),
			email: this.formData.email.trim(),
			phoneNumber: this.formData.phone.trim(),
			referenceCustomer: this.formData.referenceCustomer.trim(),
			bio: this.formData.bio.trim(),
			professionalCertificateFileIds: this.uploads.certificate.fileIds,
			universityGraduationDiplomaFileIds: this.uploads.diploma.fileIds,
			origin: this.origin,
			deviceLanguage: this.locale
		};

		this.partnerApplicationRestService.submitApplication(payload).then(
			(response) => {
				this.isSubmitting = false;
				var applicationId: any = this.extractApplicationId(response && response.body !== undefined ? response.body : response);
				this.angularRoutingHelperService.setPath('/partner-signup-success', false, false, {
					locale: this.locale,
					origin: this.origin,
					applicationId: applicationId,
					firstName: this.getFirstName(this.formData.fullName)
				});
			},
			(error) => {
				this.isSubmitting = false;
				this.submitFailed = true;
				this.submitErrorKey = this.resolveSubmitErrorKey(error);
			}
		);
	}

	hasProfileLink() {
		return !!(this.formData.linkedin.trim() || this.formData.website.trim());
	}

	hasBioLengthError() {
		var bio: any = this.formData.bio.trim();
		return !!bio && (bio.length < 100 || bio.length > 300);
	}

	hasVatNumber() {
		return !!this.formData.vatNumber.trim();
	}

	hasVatRateError() {
		if (!this.hasVatNumber()) {
			return false;
		}
		return !this.vatRatePattern.test(this.formData.vatRate.trim());
	}

	getBioLength() {
		return this.formData.bio.length;
	}

	hasUploadedFile(field: UploadField) {
		return !!this.uploads[field].fileIds.length;
	}

	isUploading(field: UploadField) {
		return !!this.uploads[field].uploading;
	}

	removeUploadedFile(field: UploadField, fileIndex: number) {
		this.uploads[field].files.splice(fileIndex, 1);
		this.uploads[field].fileIds.splice(fileIndex, 1);
	}

	private createUploadState() {
		return {
			fileIds: [],
			files: [],
			uploading: false,
			errorKey: undefined
		};
	}

	private normalizeVatRate() {
		var vatRate = this.formData.vatRate.trim();
		if (!vatRate) {
			return '';
		}
		return Number(vatRate).toFixed(1);
	}

	private uploadSelectedFiles(field: UploadField, files: Array<File>, index: number, input: HTMLInputElement) {
		if (index >= files.length) {
			this.uploads[field].uploading = false;
			input.value = '';
			return;
		}

		var file: File = files[index];
		var extension: any = (file.name.split('.').pop() || '').toLowerCase();
		if (this.allowedFileExtensions.indexOf(extension) === -1) {
			this.uploads[field].uploading = false;
			this.uploads[field].errorKey = 'PartnerSignup.Invalid_File_Type';
			input.value = '';
			return;
		}
		if (file.size > this.maxFileSizeInBytes) {
			this.uploads[field].uploading = false;
			this.uploads[field].errorKey = 'PartnerSignup.File_Too_Large';
			input.value = '';
			return;
		}

		this.partnerApplicationRestService.uploadFile(file).then(
			(response) => {
				var fileId: any = this.extractFileId(response && response.body !== undefined ? response.body : response);
				if (fileId === undefined || fileId === null || fileId === '') {
					this.uploads[field].uploading = false;
					this.uploads[field].errorKey = 'PartnerSignup.Upload_Failed';
					input.value = '';
					return;
				}
				this.uploads[field].files.push({
					fileId: fileId,
					fileName: file.name
				});
				this.uploads[field].fileIds.push(fileId);
				this.uploadSelectedFiles(field, files, index + 1, input);
			},
			() => {
				this.uploads[field].uploading = false;
				this.uploads[field].errorKey = 'PartnerSignup.Upload_Failed';
				input.value = '';
			}
		);
	}

	private extractFileId(responseBody: any) {
		if (responseBody === undefined || responseBody === null) {
			return undefined;
		}
		if (typeof responseBody === 'number' || typeof responseBody === 'string') {
			return responseBody;
		}
		return responseBody.id || responseBody.fileId || responseBody.partnerApplicationFileId || responseBody.partner_application_file_id;
	}

	private extractApplicationId(responseBody: any) {
		if (responseBody === undefined || responseBody === null || responseBody === '') {
			return undefined;
		}
		if (typeof responseBody === 'number') {
			return responseBody;
		}
		if (typeof responseBody === 'string') {
			return responseBody;
		}
		return responseBody.id || responseBody.applicationId || responseBody.partnerApplicationId;
	}

	private resolveSubmitErrorKey(error: any) {
		var errorCode: any = error && error.error ? error.error.errorCode : undefined;
		if (!errorCode && error && error.error && error.error.length) {
			var validationError: any = error.error[0];
			if (validationError.model === 'PartnerApplication' && validationError.property === 'Email' && validationError.message === 'Unavailable') {
				errorCode = 'PARTNER_APPLICATION_EMAIL_UNAVAILABLE';
			}
		}
		if (errorCode === 'PARTNER_APPLICATION_EMAIL_UNAVAILABLE') {
			return 'PartnerSignup.Email_Unavailable';
		}
		return 'PartnerSignup.Submit_Failed';
	}

	private getFirstName(fullName: string) {
		return (fullName || '').trim().split(/\s+/)[0] || '';
	}

	private determineLocation(queryParams) {
		var host: any = window.location.hostname;
		var url: any = window.location.href;
		var requestedOrigin: any = queryParams['origin'];
		if (host.includes('minudoc')) {
			this.origin = host.includes('minudoc.fi') ? 'minudoc.fi' : 'minudoc.ee';
			this.product = url.includes('eelvisiit') ? 'PREVISIT' : 'MINUDOC';
			this.headerClass = '';
			this.brandName = this.product === 'PREVISIT' ? 'eelvisiit' : 'MinuDoc';
			return;
		}
		if (requestedOrigin && requestedOrigin.includes('minudoc')) {
			this.origin = requestedOrigin;
			this.product = 'MINUDOC';
			this.headerClass = '';
			this.brandName = 'MinuDoc';
			return;
		}
		this.origin = host.includes('siffi') ? 'siffi.com' : (requestedOrigin || 'siffi.com');
		this.product = 'SIFFI';
		this.headerClass = 'o-container c-container';
		this.brandName = 'Siffi';
	}

	private resolveLogoSuffix(locale: string) {
		var normalizedLocale: any = this.appLanguageService.normalizeLocale(locale);
		var languageCode: any = (normalizedLocale || 'en_GB').split('_')[0];
		if (['en', 'et', 'ru', 'fi'].indexOf(languageCode) === -1) {
			return 'en';
		}
		return languageCode;
	}
}
